/**
 * PR-CORE-01 — Product Runtime Truth authority.
 *
 * Derives the single machine-readable product/release truth from the canonical
 * product registry. The registry remains the data owner; this module defines
 * the fixed state enum, the derivation, the evidence schema and validation so
 * README/docs/UI cannot drift into stronger claims than the source proves.
 *
 * Never invents PASS values: anything without evidence stays at its honest
 * weaker state.
 */
import { sha256Hex } from "./sha256Sync.js";
import { findCanonicalProductEntry, listCanonicalProductEntries, } from "../canonical-product/index.js";
import { CAPABILITY_RUNTIME_STATES, PRODUCT_RUNTIME_ENVIRONMENTS, RELEASE_EVIDENCE_KINDS, } from "./productRuntimeTruthTypes.js";
import { evidenceBoundSha, evidenceKindEnvironmentCoherent, ingestReleaseEvidence, isEvidenceBoundToSource, isGitSha, isNormalizedCiStatus, isProductionDeployEvidence, validateRuntimeEquivalenceClaim, } from "./releaseEvidence.js";
export const PRODUCT_RUNTIME_TRUTH_VERSION = "prt.v1";
const ENV_RANK = {
    local: 0,
    ci: 1,
    real_device: 2,
    staging: 3,
    production: 4,
};
const STATE_SET = new Set(CAPABILITY_RUNTIME_STATES);
const ENV_SET = new Set(PRODUCT_RUNTIME_ENVIRONMENTS);
const KIND_SET = new Set(RELEASE_EVIDENCE_KINDS);
const ISO_TS_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
/**
 * Derive the truthful runtime state of a canonical capability from registry
 * status alone — i.e. with no external release evidence bound to the source
 * under test. Because LIVE requires SHA-bound production proof, this function
 * can never return LIVE; use `deriveProductRuntimeTruthEntry` for the full
 * evidence-driven derivation. Order matters: explicit disabled/missing/
 * blocked next; approval-gated capabilities surface APPROVAL_REQUIRED rather
 * than a vague "ready"; anything else that only has code/wiring is PREVIEW.
 */
export function deriveCapabilityRuntimeState(entry) {
    return deriveEntryRuntimeState(entry, []);
}
/**
 * LIVE requires BOTH canonical eligibility (`stageStatus.production_proven`,
 * which itself requires runtime_proven → wired → implemented by registry
 * validation) AND a `production_deploy` evidence record strictly SHA-bound to
 * the source under test. Real-device/owner proof strengthens truth but can
 * never substitute for an actual production deploy — so `state=LIVE` can
 * never coexist with `deployStatus=not_deployed`. Unbound or stale-SHA
 * evidence can never promote a capability to LIVE.
 */
function deriveEntryRuntimeState(entry, boundEvidence) {
    const s = entry.stageStatus;
    if (entry.status === "missing" || !s.implemented)
        return "NOT_CONFIGURED";
    if (entry.status === "disabled")
        return "DISABLED";
    if (entry.status === "blocked" || s.blocked) {
        return entry.ownerActionNeeded.trim().length > 0 ? "OWNER_ACTION_REQUIRED" : "BLOCKED";
    }
    const provenHere = s.production_proven &&
        boundEvidence.some((e) => e.kind === "production_deploy" && e.environment === "production");
    if (provenHere)
        return "LIVE";
    if (entry.requiresApproval)
        return "APPROVAL_REQUIRED";
    return "PREVIEW";
}
function mapEvidenceKind(stage) {
    switch (stage) {
        case "runtime_proven":
        case "production_proven":
            // Registry stage evidence is a test/inspection record, never a real
            // deployment — it must not synthesize the production_deploy kind that
            // LIVE requires.
            return "integration_test";
        default:
            return "code_inspection";
    }
}
/** Derive one truth entry from a canonical registry entry. */
export function deriveProductRuntimeTruthEntry(entry, extraEvidence = [], sourceSha = "unversioned", equivalentShas) {
    const s = entry.stageStatus;
    const evidence = [];
    for (const stage of ["implemented", "wired", "runtime_proven", "production_proven"]) {
        const rec = entry.stageEvidence[stage];
        if (!rec)
            continue;
        evidence.push({
            environment: stage === "production_proven" ? "production" : stage === "runtime_proven" ? "ci" : "local",
            checkedAt: rec.checkedAt ?? "",
            kind: mapEvidenceKind(stage),
            ...(rec.checkedSha ? { headSha: rec.checkedSha } : {}),
            ...(rec.testFile ? { artifactRefs: [rec.testFile] } : {}),
        });
    }
    for (const ev of extraEvidence)
        evidence.push(ev);
    const boundEvidence = evidence.filter((e) => isEvidenceBoundToSource(e, sourceSha, equivalentShas));
    const state = deriveEntryRuntimeState(entry, boundEvidence);
    const boundDeploy = boundEvidence.find((e) => e.kind === "production_deploy" && e.environment === "production") ??
        boundEvidence.find((e) => e.kind === "staging_deploy" && e.environment === "staging");
    // The "production" environment claim is reserved for LIVE. A capability that
    // rode a production deploy but is not production-proven reports at most
    // "staging" — the truth never advertises a scope the state cannot claim.
    const environment = state === "LIVE"
        ? "production"
        : boundDeploy !== undefined || boundEvidence.some((e) => e.environment === "staging")
            ? "staging"
            : s.runtime_proven
                ? "ci"
                : "local";
    const blocked = state === "BLOCKED" || state === "OWNER_ACTION_REQUIRED";
    return Object.freeze({
        capabilityId: entry.id,
        state,
        environment,
        sourceReady: s.implemented,
        runtimeWired: s.wired,
        deployed: boundDeploy !== undefined,
        productionProven: state === "LIVE",
        evidence: Object.freeze(evidence),
        ...(boundDeploy?.deploySha ? { deploySha: boundDeploy.deploySha } : {}),
        ...(blocked && entry.ownerActionNeeded.trim().length > 0
            ? { blockerReason: entry.ownerActionNeeded }
            : {}),
    });
}
/**
 * Content checksum over the canonical product registry inputs — the value a
 * generated doc must reproduce to be considered fresh. Deterministic: same
 * registry always yields the same checksum regardless of git state.
 */
export function computeProductTruthRegistryChecksum() {
    const canonical = listCanonicalProductEntries()
        .filter((e) => !e.deprecated)
        .map((e) => ({
        id: e.id,
        category: e.category,
        implemented: e.stageStatus.implemented,
        wired: e.stageStatus.wired,
        runtime_proven: e.stageStatus.runtime_proven,
        production_proven: e.stageStatus.production_proven,
        blocked: e.stageStatus.blocked,
        status: e.status,
        requiresApproval: e.requiresApproval,
        ownerActionNeeded: e.ownerActionNeeded,
    }))
        .sort((a, b) => a.id.localeCompare(b.id));
    return sha256Hex(JSON.stringify(canonical));
}
/**
 * Resolve the source SHA the truth snapshot was built from. CI/deploy set
 * `SMART_SHIKKA_SOURCE_SHA`; local builds may stay "unversioned" — a doc that
 * claims a real SHA must carry a real one.
 */
export function resolveProductTruthSourceSha(env) {
    const sha = env?.SMART_SHIKKA_SOURCE_SHA?.trim();
    return sha && sha.length > 0 ? sha : "unversioned";
}
const EXACT_GIT_SHA_RE = /^[0-9a-f]{40}$/;
/**
 * A behavior/acceptance gate may treat a source SHA as "exact" only when it
 * is a full 40-hex Git SHA. Anything else — "unversioned", empty, short, or
 * non-hex — can never satisfy an exact-SHA evidence binding.
 */
export function isExactGitSha(value) {
    return typeof value === "string" && EXACT_GIT_SHA_RE.test(value);
}
/**
 * Build the whole-product runtime truth. `sourceSha` is the tree the snapshot
 * was derived from; callers must pass the real current SHA — never a guess.
 * There is exactly one external-evidence input: `releaseEvidence`, ingested
 * through the canonical strict validator — there is no unvalidated override
 * channel.
 */
export function buildProductRuntimeTruth(input) {
    const ingested = ingestReleaseEvidence(input.releaseEvidence, input.sourceSha, input.runtimeEquivalences);
    const mergedOverrides = ingested.evidenceOverrides;
    const equivalentShas = new Set(input.runtimeEquivalences?.map((c) => c.evidenceSha));
    const entries = listCanonicalProductEntries()
        .filter((e) => !e.deprecated)
        .map((e) => deriveProductRuntimeTruthEntry(e, mergedOverrides[e.id], input.sourceSha, equivalentShas));
    // When a release-evidence bundle is supplied, the ingestion authority alone
    // decides buildVerification/deployStatus — caller hints cannot strengthen
    // them. Without a bundle the explicit fields act as the caller's own
    // verified record (e.g. a CI job reporting its own run).
    const evidenceSupplied = input.releaseEvidence !== undefined;
    return Object.freeze({
        version: PRODUCT_RUNTIME_TRUTH_VERSION,
        generatedAt: input.generatedAt ?? new Date(0).toISOString(),
        sourceSha: input.sourceSha,
        registryChecksum: computeProductTruthRegistryChecksum(),
        ...(input.sourceVersion !== undefined ? { sourceVersion: input.sourceVersion } : {}),
        ...(input.sourceTreeSha !== undefined ? { sourceTreeSha: input.sourceTreeSha } : {}),
        buildVerification: evidenceSupplied
            ? ingested.buildVerification
            : (input.buildVerification ?? "unknown"),
        deployStatus: evidenceSupplied
            ? ingested.deployStatus
            : (input.deployStatus ?? "not_deployed"),
        ...(evidenceSupplied
            ? {
                evidenceIngestion: Object.freeze({
                    accepted: ingested.accepted.length,
                    rejected: ingested.rejected.length,
                }),
            }
            : {}),
        ...(ingested.deployedSha !== undefined ? { deployedSha: ingested.deployedSha } : {}),
        ...(ingested.appliedEquivalences.length > 0
            ? { runtimeEquivalence: Object.freeze(ingested.appliedEquivalences) }
            : {}),
        entries: Object.freeze(entries),
    });
}
function validateEvidence(e, where, errors) {
    if (!ENV_SET.has(e.environment))
        errors.push(`${where}: unknown environment '${e.environment}'`);
    if (!KIND_SET.has(e.kind))
        errors.push(`${where}: unknown evidence kind '${e.kind}'`);
    if (KIND_SET.has(e.kind) && ENV_SET.has(e.environment) && !evidenceKindEnvironmentCoherent(e)) {
        errors.push(`${where}: kind '${e.kind}' cannot claim environment '${e.environment}'`);
    }
    if (!ISO_TS_RE.test(e.checkedAt))
        errors.push(`${where}: evidence missing ISO checkedAt`);
    if ((e.kind === "production_deploy" || e.kind === "staging_deploy") && !e.headSha && !e.deploySha) {
        errors.push(`${where}: ${e.kind} evidence requires headSha or deploySha`);
    }
    if (e.kind === "ci_run" && (!e.ciRunId || !isNormalizedCiStatus(e.ciStatus))) {
        errors.push(`${where}: ci_run evidence requires ciRunId and ciStatus of 'success' or 'failure'`);
    }
}
/**
 * Validate a ProductRuntimeTruth snapshot. Fails on unknown enums, stronger
 * claims than evidence supports, missing blocker reasons and drift vs the
 * canonical product registry (every non-deprecated capability covered exactly
 * once).
 */
export function validateProductRuntimeTruth(truth) {
    const errors = [];
    const seen = new Set();
    const equivalentShas = new Set((truth.runtimeEquivalence ?? []).map((c) => c.evidenceSha));
    const claimSeen = new Set();
    for (const claim of truth.runtimeEquivalence ?? []) {
        const reason = validateRuntimeEquivalenceClaim(claim, truth.sourceSha);
        if (reason !== undefined)
            errors.push(`runtimeEquivalence '${claim.evidenceSha}': ${reason}`);
        if (claimSeen.has(claim.evidenceSha)) {
            errors.push(`runtimeEquivalence '${claim.evidenceSha}': duplicate claim`);
        }
        claimSeen.add(claim.evidenceSha);
    }
    if (truth.deployedSha !== undefined && !isGitSha(truth.deployedSha)) {
        errors.push(`deployedSha '${truth.deployedSha}' is not a git sha`);
    }
    for (const entry of truth.entries) {
        const where = `entry '${entry.capabilityId}'`;
        if (seen.has(entry.capabilityId))
            errors.push(`${where}: duplicate`);
        seen.add(entry.capabilityId);
        if (!STATE_SET.has(entry.state))
            errors.push(`${where}: unknown state '${entry.state}'`);
        if (!ENV_SET.has(entry.environment))
            errors.push(`${where}: unknown environment '${entry.environment}'`);
        const canonical = findCanonicalProductEntry(entry.capabilityId);
        if (!canonical) {
            errors.push(`${where}: no canonical product registry entry`);
            continue;
        }
        if (canonical.deprecated)
            errors.push(`${where}: deprecated capability must not appear in runtime truth`);
        if (entry.state === "LIVE") {
            if (!entry.productionProven || !canonical.stageStatus.production_proven) {
                errors.push(`${where}: LIVE requires productionProven on both truth entry and registry`);
            }
            const prod = entry.evidence.some((e) => isProductionDeployEvidence(e, truth.sourceSha, equivalentShas));
            if (!prod) {
                errors.push(`${where}: LIVE requires production_deploy evidence sha-bound to sourceSha`);
            }
        }
        if (entry.deploySha !== undefined &&
            entry.deploySha !== truth.sourceSha &&
            !equivalentShas.has(entry.deploySha)) {
            errors.push(`${where}: deploySha claims a different executable SHA without a runtimeEquivalence claim`);
        }
        if (entry.deployed && entry.environment !== "production" && entry.environment !== "staging") {
            errors.push(`${where}: deployed entries must be scoped to staging or production`);
        }
        if ((entry.state === "BLOCKED" || entry.state === "OWNER_ACTION_REQUIRED") && !entry.blockerReason) {
            errors.push(`${where}: ${entry.state} requires blockerReason`);
        }
        if (entry.productionProven && !entry.runtimeWired) {
            errors.push(`${where}: productionProven requires runtimeWired`);
        }
        for (const ev of entry.evidence) {
            validateEvidence(ev, where, errors);
            // A deploy claim bound to a different executable SHA is only legal via a
            // recorded runtimeEquivalence — otherwise it silently smuggles a foreign
            // deploy into this release's truth.
            if (ev.kind === "production_deploy" || ev.kind === "staging_deploy") {
                const bound = evidenceBoundSha(ev);
                if (bound !== undefined && bound !== truth.sourceSha && !equivalentShas.has(bound)) {
                    errors.push(`${where}: ${ev.kind} evidence bound to a different executable SHA without a runtimeEquivalence claim`);
                }
            }
        }
        if (entry.environment === "production" && entry.state !== "LIVE") {
            errors.push(`${where}: production-scoped entries must be LIVE`);
        }
        if (ENV_RANK[entry.environment] > ENV_RANK.ci && entry.evidence.length === 0) {
            errors.push(`${where}: real_device/staging/production scope requires evidence`);
        }
    }
    for (const canonical of listCanonicalProductEntries()) {
        if (!canonical.deprecated && !seen.has(canonical.id)) {
            errors.push(`canonical capability '${canonical.id}' is missing from runtime truth`);
        }
    }
    return Object.freeze({ valid: errors.length === 0, errors: Object.freeze(errors) });
}
//# sourceMappingURL=productRuntimeTruth.js.map